import { useCallback, useRef } from "react"
import { useAppMessage } from "./callTransport"
import { DIRECT_MESSAGE_SIGNAL_TYPE, type DirectMessageSignal } from "./callSignals"

export type DirectMessageSignalInfo = Omit<DirectMessageSignal, "type">

/**
 * Direct messages are stored through the API and normally announced over the meeting's realtime
 * panel socket. Everyone in the call is also connected to Daily, so the sender broadcasts a
 * lightweight app message too; recipients treat it exactly like the socket event. Notifications
 * then arrive even when the socket is slow, blocked, or not configured for an environment.
 */
export function useDirectMessageSignals({ enabled, onSignal }: { enabled: boolean; onSignal: (signal: DirectMessageSignalInfo) => void }) {
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled
  const onSignalRef = useRef(onSignal)
  onSignalRef.current = onSignal

  const handleAppMessage = useCallback((event: { data?: DirectMessageSignal }) => {
    const data = event?.data
    if (!enabledRef.current || data?.type !== DIRECT_MESSAGE_SIGNAL_TYPE) return
    if (typeof data.threadId !== "string" || !data.threadId) return
    onSignalRef.current({
      threadId: data.threadId,
      senderIdentityKey: typeof data.senderIdentityKey === "string" ? data.senderIdentityKey : "",
      recipientIdentityKey: typeof data.recipientIdentityKey === "string" ? data.recipientIdentityKey : "",
    })
  }, [])
  const sendAppMessage = useAppMessage<DirectMessageSignal>({ onAppMessage: handleAppMessage })

  const sendSignal = useCallback((info: DirectMessageSignalInfo) => {
    if (!enabledRef.current) return
    sendAppMessage({ type: DIRECT_MESSAGE_SIGNAL_TYPE, ...info }, "*")
  }, [sendAppMessage])

  return { sendSignal }
}
